import { isDefined } from '@sgme/fp';
import { isEqual } from 'lodash-es';
import type { Action } from 'state/actions';

import { onColumnStateChanged, onDisplayedItemsCntChanged } from '../common/blotter.reducer.utils';
import type {
  SecondaryMarketOrderBlotterColumnDefinitionReceived,
  SecondaryMarketOrderBlotterItemsFetchingFailed,
  SecondaryMarketOrderBlotterItemsReceived,
  SecondaryMarketOrderBlotterPreferenceStateLoaded,
} from './secondaryMarketOrderBlotter.action';
import type { SecondaryMarketOrderBlotterState } from './secondaryMarketOrderBlotter.model';
import { initialSecondaryMarketOrderBlotterState } from './secondaryMarketOrderBlotter.model';

// tslint:disable-next-line:cyclomatic-complexity
export function secondaryMarketOrderBlotterReducer(
  state: SecondaryMarketOrderBlotterState = initialSecondaryMarketOrderBlotterState,
  action: Action,
): SecondaryMarketOrderBlotterState {
  switch (action.type) {
    case 'SECONDARY_MARKET_ORDER_BLOTTER_COLUMN_DEFINITION_RECEIVED':
      return onColumnDefinitionReceived(state, action);
    case 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_RECEIVED':
      return onItemsReceived(state, action);
    case 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_REQUESTED':
      return onItemsRequested(state);
    case 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_FETCHING_FAILED':
      return onItemsFetchingFailed(state, action);
    case 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_GET':
      return { ...state, areBlotterPreferencesInError: false };
    case 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_LOADED':
      return onBlotterPreferenceStateLoaded(state, action);
    case 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_LOADING_FAILED':
      return { ...state, areBlotterPreferencesInError: true, canSaveBlotterPreferences: false };
    case 'SECONDARY_MARKET_ORDER_BLOTTER_COLUMN_STATE_CHANGED':
      return onColumnStateChanged(state, action);
    case 'SECONDARY_MARKET_ORDER_BLOTTER_DISPLAYED_ITEMS_CNT_CHANGED':
      return onDisplayedItemsCntChanged(state, action);
    case 'SECONDARY_MARKET_ORDER_BLOTTER_SET_COLUMN_FILTER':
      return {
        ...state,
        blotterPreferenceState: {
          ...state.blotterPreferenceState,
          columnState: [
            ...(state.blotterPreferenceState.columnState?.map(c => {
              if (c.colId === action.columnName) {
                return {
                  ...c,
                  filterModel: {
                    filterType: action.filterType,
                    filterModels: action.filterModels,
                  },
                };
              }
              return c;
            }) ?? []),
          ],
        },
      };
    default:
      return state;
  }
}

function onColumnDefinitionReceived(
  state: SecondaryMarketOrderBlotterState,
  { columnDefinition }: SecondaryMarketOrderBlotterColumnDefinitionReceived,
): SecondaryMarketOrderBlotterState {
  return { ...state, columnDefinition };
}

function onItemsRequested(state: SecondaryMarketOrderBlotterState): SecondaryMarketOrderBlotterState {
  return { ...state, loading: true, error: false };
}

function onItemsReceived(
  state: SecondaryMarketOrderBlotterState,
  { items }: SecondaryMarketOrderBlotterItemsReceived,
): SecondaryMarketOrderBlotterState {
  const newState = { ...state, loading: false, error: false };
  if (isEqual(state.items, items)) {
    return newState;
  }
  return { ...newState, items };
}

function onItemsFetchingFailed(
  state: SecondaryMarketOrderBlotterState,
  _: SecondaryMarketOrderBlotterItemsFetchingFailed,
): SecondaryMarketOrderBlotterState {
  return { ...state, error: true, loading: false, items: isDefined(state.items) ? state.items : [] };
}

function onBlotterPreferenceStateLoaded(
  state: SecondaryMarketOrderBlotterState,
  { blotterPreferenceState }: SecondaryMarketOrderBlotterPreferenceStateLoaded,
): SecondaryMarketOrderBlotterState {
  if (blotterPreferenceState.columnState === undefined) {
    return {
      ...state,
      blotterPreferenceState,
      areBlotterPreferencesInError: false,
      canSaveBlotterPreferences: true,
    };
  }
  const isAllHidden = blotterPreferenceState.columnState.every(i => (i.hide ? i.hide : false));
  return {
    ...state,
    areBlotterPreferencesInError: false,
    canSaveBlotterPreferences: true,
    blotterPreferenceState: isAllHidden
      ? {
          ...blotterPreferenceState,
          columnState: blotterPreferenceState.columnState.map(i => ({
            ...i,
            hide: false,
          })),
        }
      : blotterPreferenceState,
  };
}
